/**
 * BillingSettings Component
 * Shows subscription summary in settings with link to full billing page
 */
import { Link } from 'react-router-dom';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import Spinner from '@/components/ui/Spinner';
import { SubscriptionStatus } from '@/components/billing';
import { useSubscription, useBillingPortal, useEntitlements, useTrialStatus } from '@/hooks/useBilling';
import { useDocumentCounts } from '@/hooks/useDocuments';
import { useAuthStore } from '@/store/authStore';
import { formatDate, formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';

// Free tier upload limit
const FREE_TIER_UPLOAD_LIMIT = 10;

// Trial duration in days (from billing-entitlements.md Section 6.1)
const TRIAL_TOTAL_DAYS = 14;

/**
 * Billing settings component
 * Shows current subscription summary with link to full billing page
 */
export default function BillingSettings() {
  const { data: subscriptionData, isLoading, error } = useSubscription();
  const { data: entitlements } = useEntitlements();
  const { data: documentCounts, isLoading: countsLoading } = useDocumentCounts();
  const {
    data: trialStatus,
    isLoading: trialLoading,
    isError: trialError,
    refetch: refetchTrial,
  } = useTrialStatus();
  const billingPortal = useBillingPortal();
  // WP-BILLING-21: only the owner manages billing (the API returns 403 to members).
  const isOwner = useAuthStore((state) => state.user?.role === 'owner');

  const handleManageBilling = async () => {
    try {
      const result = await billingPortal.mutateAsync(window.location.href);
      window.location.href = result.url;
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading || countsLoading || trialLoading) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Alert variant="error">
          Failed to load subscription information. Please try again later.
        </Alert>
      </Card>
    );
  }

  // WP-BILLING-20: the current subscription, past_due and unpaid included. A
  // customer whose card failed is not on the free plan and must see how to fix it.
  const subscription = subscriptionData?.subscription;

  // Get upload usage from document counts API (not entitlements)
  // Entitlements don't track usage - the /documents/counts endpoint provides actual counts
  const uploadsUsed = documentCounts?.all ?? 0;
  const uploadLimit = FREE_TIER_UPLOAD_LIMIT;

  // Check for unlimited uploads entitlement using correct feature key
  const hasUnlimitedUploads = entitlements?.some(
    (e) => e.featureKey === 'documents:upload' && e.isActive
  ) ?? false;

  // No subscription - show free tier info with usage
  if (!subscription) {
    const usagePercent = Math.min((uploadsUsed / uploadLimit) * 100, 100);
    const isNearLimit = uploadsUsed >= uploadLimit * 0.8;
    const isAtLimit = uploadsUsed >= uploadLimit;

    return (
      <Card>
        <CardHeader
          title="Subscription"
          description="You're on the free plan"
        />

        {/* Trial status display - show when trial data is available and user is trialing */}
        {trialStatus?.isTrialing && !trialStatus.isExpired && trialStatus.daysRemaining > 0 && (
          <div
            className={cn(
              'mb-4 p-3 rounded-lg border',
              trialStatus.daysRemaining <= 1
                ? 'bg-red-50 border-red-200'
                : trialStatus.daysRemaining <= 3
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-blue-50 border-blue-200'
            )}
            role="status"
            aria-live="polite"
          >
            <div className="flex items-center gap-2">
              <ClockIcon
                className={cn(
                  'h-5 w-5 flex-shrink-0',
                  trialStatus.daysRemaining <= 1
                    ? 'text-red-500'
                    : trialStatus.daysRemaining <= 3
                      ? 'text-yellow-500'
                      : 'text-blue-500'
                )}
              />
              <span
                className={cn(
                  'text-sm font-medium',
                  trialStatus.daysRemaining <= 1
                    ? 'text-red-700'
                    : trialStatus.daysRemaining <= 3
                      ? 'text-yellow-700'
                      : 'text-blue-700'
                )}
              >
                {(() => {
                  const currentDay = TRIAL_TOTAL_DAYS - trialStatus.daysRemaining + 1;
                  if (trialStatus.daysRemaining === 1) {
                    return `Day ${currentDay} of ${TRIAL_TOTAL_DAYS} — Your free trial ends today!`;
                  }
                  return `Day ${currentDay} of ${TRIAL_TOTAL_DAYS} — ${trialStatus.daysRemaining} days remaining`;
                })()}
              </span>
            </div>
          </div>
        )}

        {/* Trial status error - show retry option */}
        {trialError && (
          <div className="mb-4 p-3 rounded-lg border bg-gray-50 border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Unable to load trial status
              </span>
              <button
                onClick={() => refetchTrial()}
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Trial expired display */}
        {trialStatus?.isExpired && (
          <div
            className="mb-4 p-3 rounded-lg border bg-red-50 border-red-200"
            role="alert"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <WarningIcon className="h-5 w-5 flex-shrink-0 text-red-500" />
                <span className="text-sm font-medium text-red-700">
                  Your free trial has expired
                </span>
              </div>
              <Link to="/billing">
                <Button variant="primary" size="sm">
                  Upgrade Now
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Usage indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Document Uploads</span>
            <span className={`text-sm font-medium ${isAtLimit ? 'text-red-600' : isNearLimit ? 'text-yellow-600' : 'text-gray-600'}`}>
              {uploadsUsed} / {uploadLimit}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-yellow-500' : 'bg-primary-500'
              }`}
              style={{ width: `${usagePercent}%` }}
              role="progressbar"
              aria-valuenow={uploadsUsed}
              aria-valuemin={0}
              aria-valuemax={uploadLimit}
              aria-label={`${uploadsUsed} of ${uploadLimit} uploads used`}
            />
          </div>
          {isAtLimit && (
            <p className="mt-2 text-sm text-red-600">
              You've reached your upload limit. Upgrade to continue uploading documents.
            </p>
          )}
          {isNearLimit && !isAtLimit && (
            <p className="mt-2 text-sm text-yellow-600">
              You're approaching your upload limit. Consider upgrading for unlimited uploads.
            </p>
          )}
        </div>

        <div className="text-center py-4 border-t border-gray-100">
          <div className="mb-4">
            <svg
              className="mx-auto h-10 w-10 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
          <p className="text-gray-600 mb-4">
            Upgrade to Pro for unlimited uploads and premium features.
          </p>
          <Link to="/billing">
            <Button variant="primary">Upgrade Now</Button>
          </Link>
        </div>
      </Card>
    );
  }

  // Has subscription - show summary
  const willCancel = subscription.cancelAtPeriodEnd;
  const isPastDue = subscription.status === 'past_due';
  const isUnpaid = subscription.status === 'unpaid';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Subscription"
          description="Your current plan and billing information"
          action={
            <Link to="/billing">
              <Button variant="outline" size="sm">
                Manage Plan
              </Button>
            </Link>
          }
        />

        {/* Warning for past due */}
        {isPastDue && (
          <Alert variant="error" className="mb-4">
            Your payment is past due. Please update your payment method to avoid service interruption.
          </Alert>
        )}
        {isUnpaid && (
          <Alert variant="error" className="mb-4">
            Your subscription is unpaid. Premium features are paused and your data is read-only until you
            update your payment method.
          </Alert>
        )}

        {/* Warning for pending cancellation */}
        {willCancel && (
          <Alert variant="warning" className="mb-4">
            Your subscription is scheduled to cancel on {formatDate(subscription.currentPeriodEnd)}.
            <Link to="/billing" className="ml-2 font-medium underline">
              Resume subscription
            </Link>
          </Alert>
        )}

        <div className="space-y-4">
          {/* Plan and Status Row */}
          <div className="flex items-start justify-between py-3 border-b border-gray-100">
            <div>
              <div className="font-medium text-gray-900">{subscription.planName}</div>
              <div className="text-sm text-gray-500">
                {/* WP-BUG-BILLING-SYNC-2: Use planAmount/planCurrency/planInterval from backend */}
                {formatCurrency(subscription.planAmount / 100, subscription.planCurrency)}
                {' / '}
                {subscription.planInterval}
              </div>
            </div>
            <SubscriptionStatus
              status={subscription.status}
              trialEnd={subscription.trialEnd}
              cancelAt={willCancel ? subscription.currentPeriodEnd : null}
            />
          </div>

          {/* Next billing */}
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div className="text-sm font-medium text-gray-700">
              {willCancel ? 'Access ends' : 'Next billing date'}
            </div>
            <div className="text-sm text-gray-900">
              {/* WP-BUG-BILLING-SYNC-2: Handle null dates gracefully */}
              {subscription.currentPeriodEnd ? formatDate(subscription.currentPeriodEnd) : 'Syncing...'}
            </div>
          </div>

          {/* Unlimited uploads badge for paid users */}
          {hasUnlimitedUploads && (
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="text-sm font-medium text-gray-700">Document Uploads</div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <svg
                  className="w-3 h-3 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Unlimited
              </span>
            </div>
          )}

          {/* Action buttons */}
          {isOwner ? (
            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                variant="outline"
                onClick={handleManageBilling}
                isLoading={billingPortal.isPending}
              >
                Update Payment Method
              </Button>
              <Link to="/billing">
                <Button variant="ghost">View Billing History</Button>
              </Link>
            </div>
          ) : (
            <p className="text-sm text-gray-500 pt-2">Billing is managed by the household owner.</p>
          )}
        </div>
      </Card>
    </div>
  );
}

// ============================================================================
// Icon Components
// ============================================================================

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  );
}
